/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useState } from 'react';

import { useRetracked } from 'js/lib/retracked';

import { Button, Dialog, breakpoints } from '@coursera/cds-core';
import type { DialogProps } from '@coursera/cds-core';

import generateUUID from 'bundles/common/utils/uuid';

import _t from 'i18n!nls/xdp';

const { HeadingGroup, Content, Actions } = Dialog;

const styles = {
  paragraph: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  button: css`
    ${breakpoints.down('xs')} {
      width: 100%;
      max-width: none;
    }
  `,
  header: css`
    ${breakpoints.down('sm')} {
      padding: 0 var(--cds-spacing-600) var(--cds-spacing-300) var(--cds-spacing-600);
    }

    ${breakpoints.down('xs')} {
      padding: 0 var(--cds-spacing-200) var(--cds-spacing-300) var(--cds-spacing-200);
    }
  `,
  // This is used for overriding the global styles for enrollment button
  // https://github.com/webedx-spark/web/blob/1f575288b4765af4d84d2b5c6c0d8dee4931b2d3/static/bundles/unified-description-page-common/components/modules/hero/HeroModuleWithImage.tsx#L81-L110
  xdpModal: css`
    ${breakpoints.down('sm')} {
      button {
        width: auto !important;
      }
    }
  `,
};

export type ModalProps = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  renderInPlace?: boolean;
  keepMounted?: boolean;
  hideActions?: boolean;
  width?: DialogProps['width'];
  supportText?: string;
  closeModalTrackingName?: string;
  primaryButtonTrackingName?: string;
  buttonLabel?: string;
  onButtonClick?: () => void;
  isButtonLoading?: boolean;
};

type Props = ModalProps & {
  /** The header of the modal, this can be a string or a ReactNode.
   * Common use cases for the ReactNode include when you need to customize the heading
   * structure for accessibility, and some modals require a link in the heading.
   */
  header?: string | React.ReactNode;
  children: React.ReactNode;
};

export const useModal = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const setModalOpen = useCallback<() => $TSFixMe>(() => {
    setIsModalOpen((isOpen) => !isOpen);
  }, []);

  return {
    isModalOpen,
    setModalOpen,
  };
};

const Modal = ({
  open,
  setOpen,
  children,
  header,
  hideActions,
  supportText,
  closeModalTrackingName,
  primaryButtonTrackingName,
  keepMounted = true,
  renderInPlace = true,
  buttonLabel,
  onButtonClick,
  isButtonLoading,
  ...rest
}: Props) => {
  const retrackedTrack = useRetracked();

  const sentRetrackedEvent = (trackingName?: string) => {
    retrackedTrack({
      trackingData: {},
      trackingName: trackingName || 'close_unified_description_page_modal',
      action: 'click',
    });
  };

  const handleClose = (trackingName?: string) => {
    sentRetrackedEvent(trackingName);
    return setOpen(false);
  };

  const handleCtaClick = (trackingName?: string) => {
    if (onButtonClick) {
      onButtonClick();
      sentRetrackedEvent(trackingName);
    } else {
      handleClose(trackingName);
    }
  };

  const [id] = useState(() => generateUUID());
  const modalId = `dialog-content-${id}`;

  return (
    <Dialog
      aria-describedby={modalId}
      open={open}
      variant="standard"
      onClose={() => handleClose(closeModalTrackingName)}
      keepMounted={keepMounted}
      renderInPlace={renderInPlace}
      css={styles.xdpModal}
      {...rest}
    >
      {typeof header === 'string' ? (
        <HeadingGroup supportText={supportText}>{header}</HeadingGroup>
      ) : (
        <div css={styles.header}>{header}</div>
      )}
      <Content id={modalId}>{children}</Content>
      {!hideActions && (
        <Actions>
          <Button
            css={styles.button}
            size="medium"
            variant="primary"
            onClick={() => handleCtaClick(primaryButtonTrackingName)}
            loading={isButtonLoading}
          >
            {buttonLabel || _t('OK')}
          </Button>
        </Actions>
      )}
    </Dialog>
  );
};

export default Modal;
